import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import toast from "react-hot-toast";
import type { FictionalRule, FictionalRuleFormValues } from "../../../models/rulesFictional";
import {
  useCreateFictionalRuleMutation,
  useGetFictionalRulesQuery,
  useToggleFictionalRuleActiveMutation,
  useUpdateFictionalRuleMutation,
} from "../../../store/services/rules-fictional";
import { useGetTenantsQuery } from "../../../store/services/tenants";
import { useTenantScope } from "../../../hooks/useTenantScope";
import {
  getAdminFacingApiErrorMessage,
  getApiErrorMessage,
  getApiErrorStatus,
  getApiValidationIssues,
} from "../../../utils/functions/api-errors";
import {
  ficticiosTabConfig,
  INITIAL_FICTIONAL_MAX,
  shouldWarnInitialFictionalCapacity,
  toApiTenantId,
} from "../configs/ficticiosTabConfig";
import {
  applyOwnedRulesTenantId,
  isRulesTenantOwnedByAdmin,
  matchesRulesTenantFilter,
  ownedRulesTenantIdForAdmin,
  resolveRulesTenantDisplayName,
} from "../constants/rulesTenant";
import type { AdminRulesCategoryPanelProps, AdminRulesFilterState } from "../types";
import { RulesDataTable } from "./RulesDataTable";
import { RulesInlineFormPanel, RulesInlineFormMode } from "./RulesInlineFormPanel";

const DUPLICATE_RULE_ERROR =
  "Ya existe una regla para esta combinación de tipo de evento, subtipo y tenant.";

const RULES_LIST_LOAD_ERROR = "No se pudieron cargar las reglas de ficticios.";

const getFormSubmitErrorMessage = (submitError: unknown, fallback: string): string => {
  const issues = getApiValidationIssues(submitError) ?? [];
  const issueMessage = issues
    .map((issue) => (typeof issue.message === "string" ? issue.message.trim() : ""))
    .find(Boolean);

  if (issueMessage) {
    return issueMessage;
  }

  return getApiErrorMessage(submitError) ?? fallback;
};

const isDuplicateRule = (
  rows: FictionalRule[],
  values: FictionalRuleFormValues,
  editingRowId: number | null
): boolean => {
  const formTenantId = toApiTenantId(values.tenantId);
  return rows.some(
    (row) =>
      row.id !== editingRowId &&
      row.eventTypeId === values.eventTypeId &&
      (row.eventSubTypeId ?? null) === (values.eventSubTypeId ?? null) &&
      (row.tenantId ?? null) === formTenantId
  );
};

const matchesTenantFilter = matchesRulesTenantFilter;

export const FictionalRulesTabContent = ({
  createTrigger = 0,
  onFormVisibilityChange,
}: AdminRulesCategoryPanelProps) => {
  const [filters, setFilters] = useState<AdminRulesFilterState>({});
  const [formMode, setFormMode] = useState<RulesInlineFormMode>("hidden");
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FictionalRuleFormValues>(
    ficticiosTabConfig.getEmptyFormValues()
  );
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useGetFictionalRulesQuery({});
  const { data: tenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();
  const ownedTenantId = ownedRulesTenantIdForAdmin(tenantScope.role, tenantScope.tenantId);

  const tenantNameById = useMemo(
    () =>
      new Map(
        (Array.isArray(tenants) ? tenants : []).map((tenant) => [tenant.id, tenant.provider] as const)
      ),
    [tenants]
  );

  const allRows = useMemo(() => data ?? [], [data]);
  const rows = useMemo(
    () =>
      allRows
        .filter((row) => {
          if (filters.eventTypeId != null && row.eventTypeId !== filters.eventTypeId) {
            return false;
          }
          if (filters.eventSubTypeId != null && row.eventSubTypeId !== filters.eventSubTypeId) {
            return false;
          }
          if (!isRulesTenantOwnedByAdmin(row.tenantId, ownedTenantId)) {
            return false;
          }
          if (filters.tenantId != null && !matchesTenantFilter(row.tenantId, filters.tenantId)) {
            return false;
          }
          return true;
        })
        .map((row) => ({
          ...row,
          tenantName: resolveRulesTenantDisplayName(row.tenantId, row.tenantName, tenantNameById),
        })),
    [allRows, filters.eventSubTypeId, filters.eventTypeId, filters.tenantId, ownedTenantId, tenantNameById]
  );
  const [createRule, { isLoading: isCreating }] = useCreateFictionalRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateFictionalRuleMutation();
  const [toggleActive] = useToggleFictionalRuleActiveMutation();
  const togglingRowIdsRef = useRef<Set<number>>(new Set());
  const isSubmitting = isCreating || isUpdating;
  // Solo la edición ocupa una pantalla propia; el alta se muestra inline sobre el listado.
  const isEditScreen = formMode === "edit";

  useEffect(() => {
    onFormVisibilityChange?.(isEditScreen);
    return () => onFormVisibilityChange?.(false);
  }, [isEditScreen, onFormVisibilityChange]);

  useEffect(() => {
    if (tenantScope.loading || tenantScope.locked) {
      return;
    }

    setFormMode("hidden");
    setEditingRowId(null);
    setFormError(null);
  }, [tenantScope.loading, tenantScope.locked]);

  const openEditForm = (row: FictionalRule) => {
    if (!tenantScope.locked) {
      return;
    }

    setFormMode("edit");
    setEditingRowId(row.id);
    setFormValues(ficticiosTabConfig.mapRowToFormValues(row));
    setFormError(null);
  };

  const closeForm = () => {
    setFormMode("hidden");
    setEditingRowId(null);
    setFormError(null);
  };

  // Ref inicializada con el valor del primer render: evita abrir el form de
  // creación al remontar el panel (p. ej. al cambiar de categoría y volver).
  const lastCreateTriggerRef = useRef(createTrigger);
  useEffect(() => {
    if (createTrigger > lastCreateTriggerRef.current && tenantScope.locked) {
      setFormMode("create");
      setEditingRowId(null);
      setFormValues(applyOwnedRulesTenantId(ficticiosTabConfig.getEmptyFormValues(), ownedTenantId));
      setFormError(null);
    }
    lastCreateTriggerRef.current = createTrigger;
  }, [createTrigger, ownedTenantId, tenantScope.locked]);

  const handleToggleActive = async (row: FictionalRule) => {
    if (!tenantScope.locked || togglingRowIdsRef.current.has(row.id)) {
      return;
    }

    togglingRowIdsRef.current.add(row.id);

    try {
      const result = await toggleActive(row.id).unwrap();
      toast.success(result.active ? "Regla activada" : "Regla desactivada");
    } catch (toggleError) {
      toast.error(
        getAdminFacingApiErrorMessage(toggleError, "No se pudo actualizar el estado de la regla.")
      );
    } finally {
      togglingRowIdsRef.current.delete(row.id);
    }
  };

  const handleSubmit = async () => {
    if (!tenantScope.locked) {
      return;
    }

    const submitValues = applyOwnedRulesTenantId(formValues, ownedTenantId);
    const validationError = ficticiosTabConfig.validateForm?.(submitValues) ?? null;
    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (isDuplicateRule(allRows, submitValues, editingRowId)) {
      setFormError(DUPLICATE_RULE_ERROR);
      return;
    }

    setFormError(null);

    const capacity = INITIAL_FICTIONAL_MAX;
    if (shouldWarnInitialFictionalCapacity(Number(submitValues.initialFictional), capacity)) {
      toast("Los ficticios iniciales superan el 80% de la capacidad del evento.", {
        icon: "⚠️",
      });
    }

    try {
      if (formMode === "edit" && editingRowId != null) {
        await updateRule(
          ficticiosTabConfig.mapFormToUpdatePayload?.(submitValues, editingRowId) as Parameters<
            typeof updateRule
          >[0]
        ).unwrap();
        toast.success("Regla actualizada");
      } else {
        const result = await createRule(
          ficticiosTabConfig.mapFormToCreatePayload?.(submitValues) as Parameters<typeof createRule>[0]
        ).unwrap();

        if (result) {
          toast.success("Regla creada");
        }
      }

      closeForm();
    } catch (submitError) {
      const status = getApiErrorStatus(submitError);
      const numericStatus =
        typeof status === "number" ? status : Number.parseInt(String(status ?? ""), 10);
      const isValidationLikeError =
        numericStatus === 400 || numericStatus === 409 || numericStatus === 404;
      const message = isValidationLikeError
        ? numericStatus === 409
          ? DUPLICATE_RULE_ERROR
          : getFormSubmitErrorMessage(
              submitError,
              "No se pudo guardar la regla. Revisá los datos ingresados."
            )
        : getAdminFacingApiErrorMessage(submitError, "No se pudo guardar la regla.");

      if (isValidationLikeError) {
        setFormError(message);
        toast(message, { icon: "⚠️" });
        return;
      }

      setFormError(message);
      toast.error(message);
    }
  };

  if (isEditScreen) {
    return (
      <div className="flex flex-column gap-3">
        <Button
          label="Volver al listado"
          icon="pi pi-arrow-left"
          severity="secondary"
          text
          className="align-self-start"
          onClick={closeForm}
          disabled={isSubmitting}
        />

        <RulesInlineFormPanel<FictionalRuleFormValues>
          mode={formMode}
          values={formValues}
          onChange={setFormValues}
          customFields={ficticiosTabConfig.inlineFormFields}
          scopeFields={ficticiosTabConfig.scopeFields}
          onSubmit={handleSubmit}
          onCancel={closeForm}
          isSubmitting={isSubmitting}
          errorMessage={formError}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      {formMode === "create" && (
        <RulesInlineFormPanel<FictionalRuleFormValues>
          mode="create"
          values={formValues}
          onChange={setFormValues}
          customFields={ficticiosTabConfig.inlineFormFields}
          scopeFields={ficticiosTabConfig.scopeFields}
          onSubmit={handleSubmit}
          onCancel={closeForm}
          isSubmitting={isSubmitting}
          errorMessage={formError}
        />
      )}

      {isError && (
        <div className="flex align-items-center justify-content-between p-3 border-round border-1 border-red-200 bg-red-50">
          <span className="text-red-600 font-medium">
            {getAdminFacingApiErrorMessage(error, RULES_LIST_LOAD_ERROR)}
          </span>
          <Button
            label="Reintentar"
            icon="pi pi-refresh"
            severity="danger"
            outlined
            size="small"
            onClick={() => refetch()}
          />
        </div>
      )}

      <RulesDataTable
        rows={rows}
        loading={isLoading && data === undefined}
        filters={filters}
        onFiltersChange={setFilters}
        customColumns={ficticiosTabConfig.customColumns}
        onEdit={openEditForm}
        onToggleActive={handleToggleActive}
        mutationsDisabled={!tenantScope.locked}
      />
    </div>
  );
};
